import { getCurrentAuthMethod } from 'auth/auth-methods/current-auth-method';
import { useConfig } from 'providers/config/ConfigProvider';
import { useState, useCallback, useEffect } from 'react';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { TokenResponse } from '@sanlam-glacier/app-common/types/auth/token-response.type.js';
import { UserInfo } from '@sanlam-glacier/app-common/types/auth/user-info.type.js';
import { IdentificationState } from '@sanlam-glacier/app-common/types/auth/identification-state.type.js';
import { AuthenticationMethod } from 'auth/auth-methods/authentication-method.type';

type UseAuthenticationResult = {
  isError: boolean;
  isAuthLoading: boolean;
};

type UseAuthenticationProps = {
  token: TokenResponse | undefined;
  signedInUser: UserInfo | undefined;
  authApi: AuthenticationMethod;
  identificationState: IdentificationState;
  setIdentificationState: React.Dispatch<React.SetStateAction<IdentificationState>>;
};

export const useAuthentication = ({
  token,
  signedInUser,
  authApi,
  identificationState,
  setIdentificationState
}: UseAuthenticationProps): UseAuthenticationResult => {
  const env = useConfig();

  const [isError, setIsError] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const pageLoadAuthenticate = useCallback(async () => {
    const { isUsingClient, isUsingAfSso } = getCurrentAuthMethod(env);

    if ((isUsingClient || isUsingAfSso) && identificationState === 'authenticated') {
      const isSuccess = await authApi.getAccess(token!);
      setIsError(!isSuccess);
    }
  }, [env, token, identificationState]);

  useEffect(() => {
    pageLoadAuthenticate()
      .catch((error) => {
        logger.error({ message: 'Error authenticating user', object: error });
        setIsError(true);
      })
      .finally(() => {
        setIsAuthLoading(false);
      });
  }, [pageLoadAuthenticate]);

  useEffect(() => {
    const isUserTokenValid = token && token.token_expiry_time > new Date();
    if (isUserTokenValid) setIdentificationState('authenticated');
    else setIdentificationState('unidentified');
  }, [env, signedInUser, token]);

  return {
    isError,
    isAuthLoading
  };
};
